<script lang="ts" setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'

import { useDisciplineStore } from '@entities/customer'

import { useLessonRoomStore } from '../../model/store'
import { useRoomLiveKit } from '../../model/room-live-kit.hook'
import { useIsPresentingLiveKit } from '../../model/presenting-live-kit.hook'
import { useClassroomNotificationsLiveKit } from '../../model/notifications-live-kit-app.hook'
import { useClassroomPermissionsLiveKit } from '../../model/permissions-live-kit-app.hook'
import { useRoomLiveKitChatReceiver } from '../../model/chat-live-kit.hook'
import { useLessonRoomBooking } from '../../model/lesson-room/lesson-room-booking.hook'
import { useLessonRoomWs } from '../../model/lesson-room/lesson-room-ws.hook'
import { useLessonRoomPing } from '../../model/lesson-room/lesson-room-ping.hook'
import { useLessonRoomPresence } from '../../model/lesson-room/lesson-room-presence.hook'
import { useLessonRoomRemoved } from '../../model/lesson-room/lesson-room-removed.hook'
import { useLessonRoomTerminal } from '../../model/lesson-room/lesson-room-terminal.hook'
import { useLessonRoomAnalytics } from '../../model/lesson-room/lesson-room-analytics.hook'
import VideoTileRemote from './VideoTileRemote.vue'
import VideoTileLocal from './VideoTileLocal.vue'
import ScreenShareStage from './ScreenShareStage.vue'
import LessonRoomScreenNearpod from '../lesson-room/LessonRoomScreenNearpod.vue'
import AudioRenderer from './AudioRenderer.vue'

defineOptions({
  name: 'RoomLiveKit',
})

const {
  connectionState,
  lastDisconnectReason,
  remoteIdentities,
  isSupported,
  connect,
  disconnect,
  startAudio,
} = useRoomLiveKit({ bindLifecycle: true })

const { listen: listenNotifications } = useClassroomNotificationsLiveKit()
const { requestPermissions } = useClassroomPermissionsLiveKit()

useLessonRoomRemoved(lastDisconnectReason)

useLessonRoomTerminal({ onBeforeRedirect: disconnect })

const lessonRoomStore = useLessonRoomStore()
const disciplineStore = useDisciplineStore()

useRoomLiveKitChatReceiver((m) => {
  lessonRoomStore.addChatMessage(m)
  if (!lessonRoomStore.isChatOpen) lessonRoomStore.setChatUnread(true)
})

const { trackPageView, trackConnected, trackDisconnected } = useLessonRoomAnalytics()

watch(connectionState, (state, previous) => {
  if (state === 'connected') trackConnected(previous === 'reconnecting')
  else if (state === 'disconnected') trackDisconnected(lastDisconnectReason.value)
})

const { nearpodCode } = useLessonRoomBooking(() => lessonRoomStore.getBooking)

const bookingId = lessonRoomStore.getBooking?.id ?? 0

useLessonRoomPresence({ bookingId })

const { listen: listenBooking } = useLessonRoomWs({
  bookingId,
  disciplineId: disciplineStore.activeDiscipline?.id ?? 0,
  onUpdate: (fresh) => {
    lessonRoomStore.setBooking(fresh)
  },
})

const { start: startBookingPing } = useLessonRoomPing({
  bookingId,
  onSuccess: (fresh) => {
    lessonRoomStore.setBooking(fresh)
  },
})

const isPresenting = useIsPresentingLiveKit()
const hasRemotePeer = computed<boolean>(() => remoteIdentities.value.length > 0)

let unlistenNotifications: (() => void) | null = null

// Autoplay: resume remote audio on the first interaction with THIS document
// (covers a bypassed/reloaded entry + Safari's in-gesture requirement). One-shot.
const unlockAudio = (): void => {
  void startAudio()
  window.removeEventListener('pointerdown', unlockAudio, true)
  window.removeEventListener('keydown', unlockAudio, true)
}

onMounted(() => {
  listenBooking()
  startBookingPing()
  trackPageView()

  if (isSupported) {
    void requestPermissions()
    void connect()
    unlistenNotifications = listenNotifications()
    window.addEventListener('pointerdown', unlockAudio, { capture: true })
    window.addEventListener('keydown', unlockAudio, { capture: true })
  }
})

onUnmounted(() => {
  unlistenNotifications?.()
  window.removeEventListener('pointerdown', unlockAudio, true)
  window.removeEventListener('keydown', unlockAudio, true)
})
</script>

<template>
  <div class="room-live-kit">
    <div
      v-if="!isSupported"
      class="room-live-kit__unsupported btly-text-title-s"
    >
      <span>This browser can’t run video lessons. Please open the lesson in a recent version of Chrome, Edge, Firefox, or Safari.</span>
    </div>

    <template v-else>
      <div :class="['room-live-kit__body', { 'room-live-kit__body--presenting': isPresenting }]">
        <ScreenShareStage
          v-if="isPresenting"
          class="room-live-kit__stage"
          :nearpod-code="nearpodCode ?? ''"
        >
          <template #nearpod>
            <LessonRoomScreenNearpod :code="nearpodCode ?? ''" />
          </template>
        </ScreenShareStage>
        <div class="room-live-kit__tiles">
          <VideoTileRemote
            v-if="hasRemotePeer"
            class="room-live-kit__tile"
          />
          <VideoTileLocal class="room-live-kit__tile" />
        </div>
      </div>

      <AudioRenderer />
    </template>
  </div>
</template>

<style scoped lang="scss" src="./RoomLiveKit.scss" />
